// Fiche d'une immobilisation : caractéristiques, indicateurs, plan d'amortissement, coût des pannes.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Grid, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { AccountBalanceOutlined, DeleteOutline, EditOutlined, EventOutlined, PrintOutlined, TrendingDownOutlined, BuildOutlined } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { amortAPI } from '../../services/api';
import { apiError, dateFr, fcfa } from '../../utils/format';
import { tokens } from '../../theme';
import ImmoDialog from './ImmoDialog';

const Ligne = ({ label, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: { sm: 2 }, py: 0.8, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ fontSize: 14 }}>{children}</Box>
  </Box>
);
const M = ({ v, bold }) => <Box component="span" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', fontWeight: bold ? 700 : undefined }}>{v}</Box>;
const duree = (mois) => (mois >= 12 ? `${Math.floor(mois / 12)} an(s)${mois % 12 ? ` ${mois % 12} mois` : ''}` : `${mois} mois`);

const Immobilisation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [i, setI] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(location.state?.info || '');
  const [dlg, setDlg] = useState(false);
  const annee = new Date().getFullYear();
  const load = () => amortAPI.get(id, annee).then((r) => setI(r.data)).catch((e) => setError(apiError(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!i) return <Layout title="Immobilisation">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const supprimer = async () => {
    if (!window.confirm(`Supprimer « ${i.designation} » du registre ? (Pour une vente ou une mise au rebut, utilisez plutôt « Modifier » → État.)`)) return;
    try { const r = await amortAPI.remove(id); navigate('/amortissements', { state: { info: r.data.message } }); } catch (e) { setError(apiError(e)); }
  };

  return (
    <Layout title={i.designation} plain
      actions={(
        <>
          <Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer</Button>
          <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => setDlg(true)}>Modifier</Button>
          {isAdmin && <Button color="error" startIcon={<DeleteOutline />} onClick={supprimer}>Supprimer</Button>}
        </>
      )}>
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Chip label={i.statut_label} color={i.statut === 'EN_SERVICE' ? 'success' : 'default'} />
        <Chip variant="outlined" label={i.categorie_label} />
        {i.totalement_amorti && <Chip color="warning" variant="outlined" label="Totalement amorti" />}
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Kpi compact icon={AccountBalanceOutlined} tint="navy" label="Prix d'acquisition" value={fcfa(i.valeur_acquisition)} valueSize={21} sub={`F HT${i.valeur_residuelle ? ` · résiduelle ${fcfa(i.valeur_residuelle)}` : ''}`} /></Grid>
        <Grid item xs={6} md={3}><Kpi compact icon={EventOutlined} tint="gray" label="Mise en service · durée" value={dateFr(i.date_mise_en_service)} valueSize={21}
          sub={i.amortissable ? `${i.duree_annees} ans · taux ${String(i.taux).replace('.', ',')} % · fin ${dateFr(i.fin_amortissement)}` : 'non amortissable'} /></Grid>
        <Grid item xs={6} md={3}><Kpi compact icon={TrendingDownOutlined} tint="amber" label="Amortissements cumulés" value={fcfa(i.cumul)} valueSize={21}
          sub={i.amortissable ? `${String(i.pct_amorti).replace('.', ',')} % · annuité ${fcfa(i.annuite)} F` : '—'} /></Grid>
        <Grid item xs={6} md={3}><Kpi compact icon={AccountBalanceOutlined} tint="green" label="Valeur nette comptable" value={fcfa(i.vnc)} valueSize={21}
          sub={i.statut === 'EN_SERVICE' ? (i.amortissable && !i.totalement_amorti ? `reste ${duree(i.reste_mois)} d'amortissement` : 'à ce jour') : `à la sortie : ${fcfa(i.vnc_sortie)} F`} /></Grid>
      </Grid>
      {i.amortissable && <LinearProgress variant="determinate" value={Math.min(100, i.pct_amorti)} sx={{ height: 8, borderRadius: 4, mb: 2 }} color={i.totalement_amorti ? 'success' : 'primary'} />}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Panel title="Caractéristiques" sx={{ mb: 2 }}>
            <Ligne label="Désignation"><b>{i.designation}</b>{i.quantite > 1 ? ` × ${i.quantite}` : ''}</Ligne>
            {i.reference && <Ligne label="Référence">{i.reference}</Ligne>}
            {i.fournisseur && <Ligne label="Fournisseur">{i.fournisseur}</Ligne>}
            {i.localisation && <Ligne label="Localisation">{i.localisation}</Ligne>}
            {i.date_acquisition && <Ligne label="Acquis le">{dateFr(i.date_acquisition)}</Ligne>}
            <Ligne label="Base amortissable"><M v={`${fcfa(i.base)} F`} /></Ligne>
            {i.statut !== 'EN_SERVICE' && <Ligne label={i.statut === 'CEDE' ? 'Cédé le' : 'Mis au rebut le'}>{dateFr(i.date_sortie)}</Ligne>}
            {i.statut === 'CEDE' && i.prix_cession !== null && (
              <>
                <Ligne label="Prix de cession"><M v={`${fcfa(i.prix_cession)} F`} /></Ligne>
                <Ligne label={i.resultat_cession >= 0 ? 'Plus-value' : 'Moins-value'}><M bold v={`${fcfa(Math.abs(i.resultat_cession))} F`} /></Ligne>
              </>
            )}
            {i.notes && <Ligne label="Notes"><span style={{ whiteSpace: 'pre-line' }}>{i.notes}</span></Ligne>}
            <Ligne label="Saisi par">{i.saisi_par || '—'}</Ligne>
          </Panel>
          {i.pannes.nb > 0 && (
            <Panel title="Pannes de cet équipement">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BuildOutlined sx={{ color: tokens.steel }} />
                <Typography sx={{ fontSize: 14 }}>{i.pannes.nb} panne(s) · réparations <b>{fcfa(i.pannes.cout)} F</b>{i.pannes.perte ? <> · pertes de production <b>{fcfa(i.pannes.perte)} F</b></> : ''}</Typography>
              </Box>
              <Button size="small" sx={{ mt: 1 }} onClick={() => navigate('/pannes', { state: { q: i.designation } })}>Voir les pannes</Button>
            </Panel>
          )}
        </Grid>
        <Grid item xs={12} md={7}>
          <Panel title="Plan d'amortissement" flush>
            {i.plan.length ? (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>Exercice</TableCell><TableCell align="right">VNC début</TableCell><TableCell align="right">Dotation</TableCell>
                      <TableCell align="right">Cumul</TableCell><TableCell align="right">VNC fin</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {i.plan.map((r) => (
                      <TableRow key={r.exercice} sx={r.exercice === annee ? { bgcolor: '#EEF3FA', '& td': { fontWeight: 700 } } : {}}>
                        <TableCell>{r.exercice}{r.exercice === annee ? ' (en cours)' : ''}</TableCell>
                        <TableCell align="right"><M v={fcfa(r.vnc_debut)} /></TableCell><TableCell align="right"><M v={fcfa(r.dotation)} /></TableCell>
                        <TableCell align="right"><M v={fcfa(r.cumul)} /></TableCell><TableCell align="right"><M v={fcfa(r.vnc_fin)} /></TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${tokens.line}` } }}>
                      <TableCell>Total</TableCell><TableCell /><TableCell align="right"><M v={fcfa(i.plan.reduce((s, r) => s + r.dotation, 0))} /></TableCell><TableCell /><TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : <Typography sx={{ p: 2, fontSize: 14, color: 'text.secondary' }}>{i.amortissable ? '—' : "Non amortissable (terrain ou durée 0) : la valeur reste égale au prix d'acquisition."}</Typography>}
          </Panel>
        </Grid>
      </Grid>
      <ImmoDialog open={dlg} immo={i} categories={i.categories} statuts={i.statuts} onClose={() => setDlg(false)}
        onSaved={(r) => { setDlg(false); setInfo(r.message); load(); }} />
    </Layout>
  );
};

export default Immobilisation;
